import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FaRobot, FaSpinner } from 'react-icons/fa';
import analystApi from '../analystApi';

const ResultsPage = () => {
  // Part 1: State Management
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);

  const handleAnalyze = async () => {
    if (!prompt.trim()) {
      setError('Please enter a question to analyze.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setAnalysisResult(null);

    try {
      const response = await analystApi.post('/api/v1/analyst/prompt', { prompt: prompt.trim() });
      const data = response.data;
      // Store the entire response object
      setAnalysisResult(data);
    } catch (err) {
      if (err.response) {
        setError(err.response.data?.detail || `Request failed (HTTP ${err.response.status})`);
      } else if (err.request) {
        setError('No response from server. Please try again.');
      } else {
        setError(err.message || 'Unexpected error occurred.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Input Form Section - always visible */}
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 mb-6">
          <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 mb-2">
            Your Question
          </label>
          <textarea
            id="prompt"
            className="w-full h-28 rounded-md border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 p-3 text-gray-900 placeholder-gray-400"
            placeholder="e.g., Analyze RELIANCE stock performance and give me investment recommendations..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={isLoading}
          />
          <div className="mt-4">
            <button
              onClick={handleAnalyze}
              disabled={isLoading}
              className={`inline-flex items-center justify-center w-full sm:w-auto px-6 py-2.5 rounded-md font-medium text-white transition-colors ${
                isLoading ? 'bg-indigo-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
              }`}
            >
              {isLoading ? (
                <span className="inline-flex items-center"><FaSpinner className="animate-spin mr-2" /> Analyzing...</span>
              ) : (
                'Analyze'
              )}
            </button>
          </div>
        </div>

        {/* Results Display Section */}
        <div className="bg-white rounded-lg shadow-sm border p-6 min-h-[260px] flex items-center justify-center">
          {/* Error State */}
          {!isLoading && error && (
            <div className="w-full">
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                {error}
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="text-center w-full">
              <FaSpinner className="animate-spin text-4xl text-indigo-500 mx-auto mb-4" />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-1">
                Analyzing with proprietary models and synthesizing with AI...
              </h2>
              <p className="text-gray-600 mb-6">
                This may take a moment as we gather comprehensive data and generate insights.
              </p>
              <div className="max-w-md mx-auto text-left text-gray-700">
                <ul className="space-y-2">
                  <li className="flex items-center"><span className="w-2 h-2 rounded-full bg-sky-500 mr-3"></span>Fetching stock data...</li>
                  <li className="flex items-center"><span className="w-2 h-2 rounded-full bg-emerald-500 mr-3"></span>Running technical analysis...</li>
                  <li className="flex items-center"><span className="w-2 h-2 rounded-full bg-violet-500 mr-3"></span>Generating AI insights...</li>
                </ul>
              </div>
            </div>
          )}

          {/* Results State */}
          {!isLoading && analysisResult && !error && (
            <div className="w-full">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Analysis Results</h3>
                <div className="text-sm text-gray-500">
                  {analysisResult.symbols_analyzed && analysisResult.symbols_analyzed.length > 0 && (
                    <span className="mr-4">
                      📊 {analysisResult.symbols_analyzed.length} symbol(s) analyzed
                    </span>
                  )}
                  {analysisResult.analysis_timestamp && (
                    <span>
                      🕒 {new Date(analysisResult.analysis_timestamp).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Display the original question */}
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Your Question:</h4>
                <p className="text-gray-700">{prompt}</p>
              </div>
              
              {/* Display the analysis with enhanced Markdown support */}
              <div className="prose prose-sm max-w-none">
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    table: ({node, ...props}) => (
                      <div className="overflow-x-auto my-4">
                        <table className="w-full border-collapse bg-white rounded-lg shadow-sm border border-gray-200" {...props} />
                      </div>
                    ),
                    thead: ({node, ...props}) => (
                      <thead className="bg-gray-50 border-b border-gray-200" {...props} />
                    ),
                    th: ({node, ...props}) => (
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-r border-gray-200 last:border-r-0" {...props} />
                    ),
                    td: ({node, ...props}) => (
                      <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200 last:border-r-0 border-b border-gray-100" {...props} />
                    ),
                    tr: ({node, ...props}) => (
                      <tr className="hover:bg-gray-50 transition-colors duration-150" {...props} />
                    )
                  }}
                >
                  {analysisResult.analysis}
                </ReactMarkdown>
              </div>
            </div>
          )}

          {/* Initial State */}
          {!isLoading && !analysisResult && !error && (
            <div className="text-center">
              <FaRobot className="text-5xl text-indigo-500 mx-auto mb-4" />
              <div className="text-xl font-semibold text-gray-900">Ready to analyze</div>
              <div className="text-gray-600 mt-1">
                Enter your question above to get started with AI-powered stock analysis.
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ResultsPage;
